import { Button, Checkbox, Group, Modal, Stack, Textarea, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { SvgNegativeFeedback, SvgProvideFeedback } from '@mygpt/app/chat/components/FeedbackButton/icon';
import { feedbackType, messageRating } from '@mygpt/app/chat/services';
import { FEEDBACK_MAP } from '@mygpt/app/chat/constant';
import { useFeedbackModal } from '@mygpt/app/chat/hooks/useFeedbackModal';

export interface onSubmitValue {
  feedbackContent: string;
  feedbackMetadata: {
    type: feedbackType;
    value: string[];
  };
}

interface FeedbackModalProps {
  type: feedbackType;
  opened: boolean;
  onClose: () => void;
  handleMessages: (obj: { rating: number; messageId: string }) => void;
}

// 定义一个接口来描述目标对象的类型
interface DynamicObject {
  [key: string]: boolean;
}

type FormValuesKey = `checked_${number}`;

export interface FormValues {
  feedback: string;
  [key: FormValuesKey]: boolean; // 使用模板字面量类型定义动态键
}

export const useGetFeedbackCheckbox = () => {
  const { t } = useTranslation();

  return {
    good: FEEDBACK_MAP.good
      .filter((item) => !item.old)
      .map((item) => ({
        ...item,
        label: t(item.label),
      })),
    bad: FEEDBACK_MAP.bad
      .filter((item) => !item.old)
      .map((item) => ({
        ...item,
        label: t(item.label),
      })),
  };
};

const FeedbackModal: React.FC<FeedbackModalProps> = ({ onClose, opened, type, handleMessages }) => {
  const { t } = useTranslation();
  const feedbackCheckboxs = useGetFeedbackCheckbox();
  const { currentMessageInfo, modalType } = useFeedbackModal();
  const { aiId, sessionId, messageId, rating } = currentMessageInfo;

  const onSubmit = (values: onSubmitValue) => {
    const param = {
      ai_id: aiId,
      session_id: sessionId,
      message_id: messageId,
      rating: (modalType === 'good' ? 2 : -1) as ratingType,

      feedback_content: values.feedbackContent,
      feedback_metadata: values.feedbackMetadata,
    };

    messageRating(param)
      .then(() => {
        handleMessages && handleMessages({ rating: param.rating, messageId });
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const formCheckboxList = feedbackCheckboxs[type].reduce<DynamicObject>((obj, _, index) => {
    obj[`checked_${index}`] = false;
    return obj;
  }, {});
  const form = useForm<FormValues>({
    initialValues: {
      ...formCheckboxList,
      feedback: '',
    },
  });

  const handleSubmit = () => {
    const feedbackMetadataIndex = [];
    for (const key in form.values) {
      if (!key.startsWith('checked')) continue;
      if (form.values[key as FormValuesKey]) {
        feedbackMetadataIndex.push(key.replace(/^checked_/, ''));
      }
    }

    const feedbackMetadataValue = feedbackMetadataIndex.map((item) => {
      return feedbackCheckboxs[type][Number(item)].value;
    });

    const value = {
      feedbackContent: form.values.feedback,
      feedbackMetadata: {
        type,
        value: feedbackMetadataValue,
      },
    };
    onSubmit(value);
    onClose(); // 提交后关闭模态框
    form.reset();
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={() => {
        form.reset();
        onClose();
      }}
      title={
        <Group>
          {type === 'good' ? <SvgProvideFeedback /> : <SvgNegativeFeedback />}{' '}
          <Title order={4}>{t('chat_feedback.title')}</Title>
        </Group>
      }
    >
      <form>
        <Stack gap={'md'} mt={20}>
          {feedbackCheckboxs[type].map((item, index) => (
            <Checkbox
              key={index}
              label={item.label}
              checked={form.values[`checked_${index}`]}
              {...form.getInputProps(`checked_${index}`)}
            />
          ))}

          <Textarea
            mt={10}
            autosize
            minRows={2}
            maxRows={10}
            placeholder={t('chat_feedback.placeholder')}
            {...form.getInputProps('feedback')}
          />

          <Group justify="flex-end" mt="md">
            <Button onClick={handleSubmit}>{t('submit')}</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default FeedbackModal;
